import { AdvancedMarker, Map } from '@vis.gl/react-google-maps';
import { type Event } from '@goodneighbor/api/schemas/events/event.schema';
import { isDatePastOrEqual } from '@goodneighbor/utilities/date/index';
import EventMarker from '@/app/events/components/EventMarker';
import { CheckInQRCode } from '../CheckInQRCode';
import RegisterAndProgress from '../RegisterAndProgress/RegisterAndProgress';
import { cx } from 'class-variance-authority';
import styles from './sidebar.module.scss';
import { getEventMetaSections } from '../../services/eventMetaSection';
import { isExternalEvent } from '../../services/event';
import { GOOGLE_MAPS_MAP_ID } from '@/services/map';

export type SidebarProps = {
  event: Event;
  originalEvent?: Event | null;
  showRegistrationCounter?: boolean;
  onRegister: () => void;
  className?: string;
};

const SidebarSection = ({
  title,
  children
}: {
  title: string;
  children: React.ReactNode;
}) => {
  return (
    <div>
      <h3 className={styles.sidebarSectionTitle}>{title}</h3>
      <div className={styles.sidebarSectionContent}>{children}</div>
    </div>
  );
};

export default function Sidebar({
  event,
  originalEvent,
  showRegistrationCounter = true,
  onRegister,
  className
}: SidebarProps) {
  const isAlreadyRegistered = event?.registered ?? false;
  const isEventPast =
    (event?.endsAt && isDatePastOrEqual(new Date(event.endsAt))) || false;

  return (
    <div className={cx(styles.sidebar, className)}>
      <RegisterAndProgress
        event={event}
        onRegister={onRegister}
        showRegistrationCounter={showRegistrationCounter}
      />
      {process.env.NEXT_PUBLIC_ENABLE_QR_CHECKIN === 'true' &&
        isAlreadyRegistered &&
        event.attendanceId &&
        !isEventPast &&
        !isExternalEvent(event) && (
          <CheckInQRCode attendanceId={event.attendanceId} eventId={event.id} />
        )}
      {getEventMetaSections(event).map(({ title, content }) => (
        <SidebarSection key={title} title={title}>
          {content}
        </SidebarSection>
      ))}
      <SidebarSection title="Location">
        <div className={styles.mapContainer}>
          <Map
            defaultCenter={{
              lat: originalEvent?.coords?.coordinates[1] || 0,
              lng: originalEvent?.coords?.coordinates[0] || 0
            }}
            defaultZoom={10}
            id="EVENT_SLIDEOUT_SIDEBAR_MAP"
            mapId={GOOGLE_MAPS_MAP_ID}
            gestureHandling="none"
            disableDefaultUI
          >
            <AdvancedMarker
              position={{
                lat: originalEvent?.coords?.coordinates[1] || 0,
                lng: originalEvent?.coords?.coordinates[0] || 0
              }}
              style={{
                position: 'relative'
              }}
            >
              <EventMarker />
            </AdvancedMarker>
          </Map>
        </div>
        <br />
        {event?.city}, {event?.region}
      </SidebarSection>
    </div>
  );
}
